/**
 * DigitalLetterCard - Displays the scanned card image
 *
 * Shows the digital version of the physical letter card after
 * the QR code is scanned. Fetches image from Supabase Storage.
 *
 * Supports multi-image cards (plain + illustrated) via the `cardImages`
 * prop. Use `imageType` to select which image to display per round.
 * Falls back to legacy `imagePath` when `cardImages` is not provided.
 *
 * States: placeholder (no scan) → loading → error | image display
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, ImageOff, ScanLine } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from '@/lib/i18n';
import type { CardImage, CardImageType } from '@/services/qrCodeService';

// ============================================================================
// Types
// ============================================================================

interface DigitalLetterCardProps {
  /** Path in storage bucket (e.g., "consonant-cards/b.png") — legacy single-image */
  imagePath: string | null;
  /** Multi-image support: array of typed images from QR code */
  cardImages?: CardImage[];
  /** Which image type to display (default: 'plain'). Used with cardImages. */
  imageType?: CardImageType;
  /** Crop to show only one half of the card. 'left' = lowercase, 'right' = uppercase. */
  cropSide?: 'left' | 'right' | null;
  /** Phoneme/letter being displayed */
  phoneme?: string;
  /** Display name for the card */
  displayName?: string;
  /** Callback when image loads successfully */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: (error: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export const DigitalLetterCard: React.FC<DigitalLetterCardProps> = ({
  imagePath,
  cardImages,
  imageType = 'plain',
  cropSide = null,
  phoneme,
  displayName,
  onLoad,
  onError,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve effective image path from cardImages (preferred) or legacy imagePath.
  // Falls back: requested type → other type → legacy path → null (letter fallback).
  const effectivePath = useMemo(() => {
    if (cardImages && cardImages.length > 0) {
      const match = cardImages.find(img => img.type === imageType);
      if (match) return match.path;
      // Fallback: if illustrated requested but only plain exists (or vice versa)
      const fallback = cardImages[0];
      if (fallback) return fallback.path;
    }
    // Fall back to legacy single-image path (always treated as plain)
    return imagePath;
  }, [cardImages, imageType, imagePath]);

  // Fetch public URL for the image
  useEffect(() => {
    if (!effectivePath) {
      setImageUrl(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Get public URL from storage
    const { data } = supabase.storage
      .from('consonant-cards')
      .getPublicUrl(effectivePath);

    if (data?.publicUrl) {
      setImageUrl(data.publicUrl);
    } else {
      setError('Could not load card image');
      onError?.('Could not load card image');
    }

    setLoading(false);
  }, [effectivePath, onError]);
  
  // No card scanned yet — OR scanned but no image uploaded
  if (!effectivePath) {
    // If we have a phoneme, show a large letter card as visual fallback
    if (phoneme) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-4 min-h-0">
          <div className="w-56 h-72 rounded-2xl bg-black/[0.03] border border-black/[0.10] flex flex-col items-center justify-center shadow-2xl">
            <span className="text-8xl font-bold text-[#1C1C1E] leading-none">
              {phoneme.toUpperCase()}
            </span>
            <span className="text-lg text-[#8E8E93] mt-2 lowercase">/{phoneme}/</span>
          </div>
          {displayName && (
            <p className="mt-3 text-[#1C1C1E] text-lg font-semibold text-center flex-shrink-0">
              {displayName}
            </p>
          )}
        </div>
      );
    }

    // Truly no scan yet — show placeholder
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6">
        <div className="w-56 h-72 rounded-2xl border-2 border-dashed border-black/[0.10] flex flex-col items-center justify-center gap-3 animate-[pulse_3s_ease-in-out_infinite]">
          <ScanLine className="w-10 h-10 text-[#D4D4D4]" />
          <span className="text-white/25 text-xs font-medium tracking-wide uppercase">
            {t('Letter Card')}
          </span>
        </div>
        <p className="mt-4 text-[#8E8E93] text-sm text-center font-medium">
          {t('Your card will appear here')}
        </p>
        <p className="mt-1 text-[#C7C7CC] text-xs text-center">
          {t('Hold the QR code up to the camera above')}
        </p>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 rounded-full bg-[#FF3B30]/10 flex items-center justify-center mb-4">
          <ImageOff className="w-8 h-8 text-[#FF3B30]" />
        </div>
        <p className="text-[#FF3B30] text-sm text-center">{error}</p>
      </div>
    );
  }
  
  // Display card image
  const cropLabel = cropSide === 'left' ? t('Lowercase') : cropSide === 'right' ? t('Uppercase') : null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4 min-h-0">
      {/* Card Image - constrained to available space */}
      {cropSide ? (
        <div className="relative max-w-sm w-full flex-1 min-h-0 overflow-hidden rounded-2xl shadow-2xl">
          <img
            src={imageUrl || ''}
            alt={displayName || phoneme || 'Letter card'}
            className="block w-[200%] max-w-none h-auto"
            style={{ marginLeft: cropSide === 'right' ? '-100%' : '0' }}
            onLoad={() => onLoad?.()}
            onError={() => {
              setError('Failed to load image');
              onError?.('Failed to load image');
            }}
          />
        </div>
      ) : (
        <div className="relative max-w-sm w-full flex-1 flex items-center justify-center min-h-0">
          <img
            src={imageUrl || ''}
            alt={displayName || phoneme || 'Letter card'}
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            onLoad={() => onLoad?.()}
            onError={() => {
              setError('Failed to load image');
              onError?.('Failed to load image');
            }}
          />
        </div>
      )}

      {/* Card Label */}
      <div className="mt-3 text-center flex-shrink-0">
        {displayName && (
          <p className="text-[#1C1C1E] text-lg font-semibold">{displayName}</p>
        )}
        {cropLabel && (
          <p className="text-[#8E8E93] text-xs mt-0.5">{cropLabel} side</p>
        )}
      </div>
    </div>
  );
};

export default DigitalLetterCard;
